/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import differenceWith from 'lodash.differencewith';
import { useLocalStore } from 'mobx-react-lite';
import * as React from 'react';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { AzureCsAccountStore } from 'src/core/stores/AzureCsAccountStore';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { createResourceStoreHouse } from 'src/luis/core/stores/resourceStoreHouse';
import { ResourceStoreHouse } from 'src/luis/types/stores/core/ResourceStoreHouse';

/**
 * This hook loads and displays the assigned accounts
 * for the current app. It then fetches the Azure data
 * for those assigned accounts and returns the filled
 * account information.
 */
export const useAssignedAzureAccounts = () => {
    const { rootStore, dispatcher } = useGlobalState();

    // These are the accounts that the user assigned to this app.
    const assignedAccounts = rootStore.currentLuisAppStore.azureAccounts;

    /**
     * This is an account store house that will track the Azure information related
     * to the accounts that are assigned to the app.
     */
    const csAccountsLocalStoreHouse = useLocalStore<ResourceStoreHouse<AzureCsAccountStore>>(createResourceStoreHouse);

    // Loads the app's assigned prediction keys.
    React.useEffect(() => dispatcher.dispatch('loadAppAzureCsAccounts'), []);

    /**
     * When the assigned accounts to the apps change (due to load, add, or delete)
     * this function re-fires to fetch the Cs accounts.
     *
     * This works as follows:
     *
     * - A LUIS api returns the ids and paths of the Azure accounts that are assigned
     * to this LUIS app. This information object contains an ID and path only, but
     * no additional information (like region, SKU, state, key strings, etc). The result
     * of this call is saved in the `assignedAccounts` array.
     *
     * - To get the rest of those accounts' information, a call to fetch their Azure
     * information is made. A call per key to fetch the Azure info is needed to fetch
     * information for each account. This data is stored in a local store house,
     * namely the `csAccountsLocalStore`.
     *
     * - Whenever the user assigns or un-assigns an Azure account to the app, its respective
     * information must be fetched into the local store, or removed from it respectively.
     * There are three cases that can occur when events happen to re-render and thus re-fire
     * this hook:
     *
     *   - The `assignedAccounts` is the same length as the `csAccountLocalStore`. This means
     *   that the accounts and their information are currently in sync. Nothing needs to be done.
     *
     *   - The `assignedAccounts` array has a greater length than the `csAccountLocalStore`.
     *   That means that the user assigned a new key and its Azure info has not been
     *   fetched yet.
     *
     *   - The `assignedAccounts` array has a smaller length than the `csAccountLocalStore`.
     *   That means that an account was un-assigned, and thus its respective Azure
     *   information must be deleted.
     */
    React.useEffect(() => {
        const diff = assignedAccounts.length - csAccountsLocalStoreHouse.items.length;

        if (diff === 0) {
            return;
        }

        if (diff < 0) {
            csAccountsLocalStoreHouse.setItems(csAccountsLocalStoreHouse.items.filter(z => assignedAccounts.some(a => a.path === z.path)));

            return;
        }

        /**
         * Ensures that the accounts that are returned from the assigned
         * accounts API filter out the user's programmatic key and the
         * collaborators' programmatic key.
         *
         * The key path of the user programmatic key is returned as null.
         *
         * The key path of the collaborator programmatic key is
         * returned as the value in the Luis Constants.
         */
        const accountsToFetch = differenceWith(assignedAccounts, csAccountsLocalStoreHouse.items, (a, b) => a.path === b.path)
            .filter(a => a.path !== null && a.path !== LuisConstants.Azure.CollaboratorKeyPath)
            .map(a => a.path)
            /**
             * Filters the duplicate paths.
             * take a look: https://luisai.visualstudio.com/LUIS/_queries/edit/21557/?triage=true
             */
            .filter((p, index, arr) => arr.indexOf(p) === index);

        if (accountsToFetch.length === 1) {
            dispatcher.dispatch('getAzureCsAccountsByPaths', {
                paths: accountsToFetch,
                localStoreHouse: csAccountsLocalStoreHouse,
                mode: 'add'
            });
        }

        if (accountsToFetch.length > 1) {
            dispatcher.dispatch('getAzureCsAccountsByPaths', {
                paths: accountsToFetch,
                localStoreHouse: csAccountsLocalStoreHouse,
                mode: 'replace'
            });
        }
    }, [assignedAccounts.length, dispatcher.dispatch]);

    const assignedAzureAccounts = csAccountsLocalStoreHouse.items.filter(account =>
        assignedAccounts.some(assignedAccount => assignedAccount.id === account.id)
    );

    const { tracker } = csAccountsLocalStoreHouse;

    return { assignedAzureAccounts, isLoading: tracker.isRunning };
};
